import styles from './Hero.module.css';
import PlayIcon from '../assets/PlayIcon';
import ArrowTopRight from '../assets/arrowTopRight';
import { useParams } from "react-router-dom";
import { useRef,useEffect, useState } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText } from 'gsap/SplitText';
import ReactDOM from "react-dom"
import toSentenceCase from '../utilities/sentenceCase.js';
import Close from '../assets/Close.jsx';
import Logo from '../assets/Logo.jsx';
import VideoControlsEl from '../components/common/VideoControls.jsx';
//import { useWindowSize } from '../utilities/useWindowSize.js';

gsap.registerPlugin(ScrollTrigger, SplitText);

export default function Hero({
    heroSize, //Choices are Fullscreen, Natural Height, 3/4 and 90% screen height
    title,
    titleBackgroundColor,
    titleTextColor,
    titlePixelStyle, //none, square, rectangle
    titlePixelColor,
    subtitle,
    heroMedia,
    heroMediaPortrait,
    ctaText,
    ctaLink,
    ctaBackgroundColor,
    ctaTextColor,
    ctaShape,
    ctaType = "link",
    ctaClickFunction = null,
    ctaHdVideoLink,
    ctaSdVideoLink,
    ctaVideoTitle = null,
}) {

    const { lang } = useParams();
    //const { width, height } = useWindowSize();
    const [lightboxVisible, setLightboxVisible] = useState(false);
    const [showControls, setShowControls] = useState(false);
    const [videoSrc, setVideoSrc] = useState(null);

    // keyboard navigation to close the lightbox
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape' || event.key === 'Esc') {
                closeLightbox();
            }
        };

        if (lightboxVisible) {
            document.addEventListener('keydown', handleKeyDown);
        }

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [lightboxVisible]);

    // lightbox
    const lightboxEl = useRef();
    const lightboxMediaVideo = useRef();
    const lightboxMediaVideoSrc = useRef();
    const lightboxTitle = useRef();
    const lightboxVideoWrapEl = useRef();
    const lightboxEmbedEl = useRef();

    // handle reel lightbox
    const openLightbox = (videoHd, videoSd, videoEmbed, title) => {

        setLightboxVisible(true);
        setShowControls(true);

        if (lightboxTitle.current != "undefined" && lightboxTitle.current != null) {
            lightboxTitle.current.innerHTML = toSentenceCase(title);
        }

        // need to check for video link or embed code

        if (typeof videoHd != "undefined" && videoHd != null) {

            lightboxVideoWrapEl.current.style.display = "flex";

        } else {

            lightboxEmbedEl.current.innerHTML = videoEmbed;
            lightboxEmbedEl.current.style.display = "block";

        }

    };

    const closeLightbox = () => {
        setLightboxVisible(false);
        setShowControls(false);
        // remove elements from lightbox
        lightboxVideoWrapEl.current.style.display = "none";
        lightboxEmbedEl.current.style.display = "none";
        lightboxEmbedEl.current.innerHTML = '';

        setShowControls(false);
    }

    function LightBox() {

        return ReactDOM.createPortal(
            <div id="lightbox" className={`lightbox ${lightboxVisible ? "active" : ""}`} ref={lightboxEl}>
                <div className="header">

                    <div className="logo">
                        <Logo />
                    </div>
                    <div className="close" onClick={() => closeLightbox()}>
                        <Close /> {lang === "ko" ? "닫기" : "CLOSE"}
                    </div>
                </div>
                <div className="body">

                    <div className="videoWrap" ref={lightboxVideoWrapEl}>
                        {showControls && <VideoControlsEl hdSrc={ctaHdVideoLink} sdSrc={ctaSdVideoLink ? ctaSdVideoLink : ctaHdVideoLink} />}
                    </div>

                    <div className="embedWrap" ref={lightboxEmbedEl}>

                    </div>

                    
                </div>

                <div className="footer">

                    {ctaVideoTitle && (
                        <h2>{toSentenceCase(ctaVideoTitle)}</h2>
                    )}

                </div>
            </div>,
            document.body
        );
    }

    const getOnClickHandler = () => {
    if (ctaHdVideoLink !== "" && ctaHdVideoLink != null) {
        return () => openLightbox(ctaHdVideoLink, ctaSdVideoLink, null, ctaVideoTitle);
    }
    if (ctaType === 'lightbox') return ctaClickFunction;
        return undefined;
    };

    // setting up hero title animation
    const splitInstance = useRef(null);
    const titleEl = useRef(null);
    const titleHeadline = useRef(null);
    const titleP = useRef(null);

    const lastLang = useRef(lang);

    useEffect(() => {

        if (splitInstance.current) {

            splitInstance.current.revert();  // <- This puts the DOM back to normal
            splitInstance.current = null;
        }

        if (typeof titleEl.current != "undefined" && titleEl.current != null) {

            // SplitText text reveal by line for textBlock only element
            gsap.set(titleEl.current, {opacity: 0});
            gsap.set(titleHeadline.current, { opacity: 0 });
            gsap.set(titleP.current, {opacity: 0});

            // Check if lang changed
            const langChanged = lastLang.current !== lang;

            // Choose timeout based on lang change
            const delay = langChanged ? 1000 : 300; // e.g. 1s if lang changed, else 0.3s

            // Store current lang as previous for next render
            lastLang.current = lang;

            setTimeout(function () {

                // split the h1 into lines
                splitInstance.current = new SplitText(titleHeadline.current, {
                    type: "lines",
                });

                // set the lines down 100px with a mask
                gsap.set(splitInstance.current.lines, {
                    y: "100px",
                    autoAlpha: 0,
                    clipPath: "inset(0% 0% 100% 0%)",
                })

                const lineMargin = lang === "ko" ? "-1vw" : "-1.5vw";

                gsap.set(splitInstance.current.lines, {marginBottom: lineMargin});

                // reset the title opacity now that split is done
                gsap.set(titleHeadline.current, {opacity: 1});

                // setup the timeline to control animation
                let tl = gsap.timeline();

                tl.to(titleEl.current, {opacity: 1})
                .to(splitInstance.current.lines, {
                    duration: 0.8,
                    clipPath: "inset(0% 0% 0% 0%)",
                    autoAlpha: 1,
                    y: 0,
                    ease: "power3.out",
                    stagger: 0.2,
                })
                .to(titleP.current, {
                    opacity: 1,
                    duration: .5,
                });

            }, delay);

        }

        return () => {
            if (splitInstance.current) {
                splitInstance.current.revert();
                splitInstance.current = null;
            }
        };

    }, [lang]);

    let heroClass = styles.fullscreen;

    if (heroSize === "90% Screen") {
        heroClass = styles.ninety;
    }

    if (heroSize === "3/4 Screen") {
        heroClass = styles.seventyfive;
    }

    if (heroSize === "Natural Height") {
        heroClass = styles.naturalHeight;
    }

    const titleStyle = titlePixelStyle === "square" ? styles.square : titlePixelStyle === "rectangle" ? styles.rectangle : styles.none;
    // Auto-detect media type based on file extension
    const getMediaType = (url) => {
        if (!url) return null;
        const extension = url.split('.').pop().toLowerCase();
        const videoExtensions = ['mp4', 'webm', 'ogg', 'mov'];
        const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp'];

        if (videoExtensions.includes(extension)) return 'video';
        if (imageExtensions.includes(extension)) return 'image';
        return null;
    };

    const mediaType = getMediaType(heroMedia.fields.file.url);

    /* if portrait video is provided check screen size and load appropiate video */
    let videoMediaFile;
    const hasPortraitMedia = heroMediaPortrait ? true : false;
    if (mediaType === "video" && hasPortraitMedia && window.innerHeight > window.innerWidth) {
        videoMediaFile = heroMediaPortrait.fields.file.url;
    } else if (mediaType === "video" && hasPortraitMedia && window.innerWidth > window.innerHeight) {
        videoMediaFile = heroMedia.fields.file.url;
    } else if (mediaType === "video" && !hasPortraitMedia) {
        videoMediaFile = heroMedia.fields.file.url;
    }


    return (
        <section>
            <div onClick={getOnClickHandler()} className={`${styles.hero} ${lang === "ko" && styles.lang_ko} ${ctaType === "lightbox" && styles.hero_click} ${heroClass} ${titleBackgroundColor === '#fafafa' ? styles.pull_up : ''}`}>
                <div className={`${styles.mediawrap}`}>
                    {/* Conditional rendering based on detected media type */}
                    {heroMedia && mediaType === "video" && (
                        <video className={styles.video} autoPlay loop muted playsInline>
                            <source src={videoMediaFile} type="video/mp4" />
                        </video>
                    )}

                    {heroMedia && mediaType === "image" && (
                        <img
                            className={styles.image}
                            src={heroMedia.fields.file.url}
                            alt={heroMedia.fields.title}
                            width={heroMedia.fields.file.details.image.width}
                            height={heroMedia.fields.file.details.image.height}
                        />
                    )}
                </div>

                {title && (
                    <div ref={titleEl} className={`${styles.titleCard} ${titleStyle}`} style={{ backgroundColor: titleBackgroundColor }}>

                        <div className={styles.titlePixel} style={{ backgroundColor: titlePixelColor }}>

                        </div>
                        <h1 ref={titleHeadline} className={styles.title} style={{ color: titleTextColor }}>{title}</h1>
                        <p ref={titleP} className={styles.subtitle} style={{ color: titleTextColor }}>{subtitle}</p>
                    </div>
                )}
                {ctaText && (
                    <div className={`cell ${styles.ctaContainer}`} style={{ backgroundColor: ctaBackgroundColor }}>
                        {ctaLink && ctaLink != "" && (
                            <a className={`ctaBox ${styles.cta}`} href={`/${lang}${ctaLink}`} style={{ color: ctaTextColor }}>
                                <div className="contents">
                                    {ctaText}
                                    {ctaShape === "Play Icon" && (
                                        <PlayIcon color={ctaTextColor} />
                                    )}
                                    {ctaShape === "Arrow" && (
                                        <ArrowTopRight color={ctaTextColor} />
                                    )}
                                </div>
                            </a>
                        )}
                        {ctaType === "lightbox" && (
                            <div onClick={ctaClickFunction} className={`ctaBox ${styles.cta}`} style={{ color: ctaTextColor }}>
                                <div className="contents">
                                    {ctaText} 
                                    {ctaShape === "Play Icon" && (
                                        <PlayIcon color={ctaTextColor} />
                                    )}
                                    {ctaShape === "Arrow" && (
                                        <ArrowTopRight color={ctaTextColor} />
                                    )}
                                </div>
                            </div>
                        )}
                        {ctaHdVideoLink && (
                            <div className={`ctaBox ${styles.cta}`} style={{ color: ctaTextColor }}>
                                <div className="contents">
                                    {ctaText}
                                    {ctaShape === "Play Icon" && (
                                        <PlayIcon color={ctaTextColor} />
                                    )}
                                    {ctaShape === "Arrow" && (
                                        <ArrowTopRight color={ctaTextColor} />
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
            <LightBox />
        </section>
    )

}